import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import EventRow from "./EventRow";
import AddEventModal from "./AddEventModal";
import { Event } from "api/canprospect/event";
import getProspectHistoryEvents from "api/canprospect/getProspectHistoryEvents";
import styles from "./events.module.css";
import getProspectHistoryEventTypes from "api/canprospect/getProspectHistoryEventTypes";
import { useNavigate } from "react-router-dom";

interface EventsTableProps {
  prospectId: number | null;
  prospectStatusId: number | null;
}

interface ProspectHistoryEventType {
  Id: number;
  Type: string;
}

function EventsTable({ prospectId, prospectStatusId }: EventsTableProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [eventTypes, setEventTypes] = useState<ProspectHistoryEventType[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const { eventTypes: fetchedEventTypes } =
          await getProspectHistoryEventTypes();
        setEventTypes(fetchedEventTypes);
      } catch (error) {
        console.error("Error fetching prospect history event types:", error);
      }
    };

    fetchEventTypes();
  }, []);

  const fetchEvents = async () => {
    if (prospectId === null) {
      setError("Invalid prospect ID.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const historyEvents = await getProspectHistoryEvents(prospectId);
      const mappedEvents = historyEvents.map((event: any) => {
        const dateTime = event.Date.trim();
        const lastSpaceIndex = dateTime.lastIndexOf(" ");
        const date = dateTime.substring(0, lastSpaceIndex).trim();
        const time = dateTime.substring(lastSpaceIndex + 1).trim();

        return {
          id: event.Id,
          prospect: event.ProspectStatus,
          consultant:
            event.Consultants?.map((c: any) => c.Name).join(", ") || "N/A",
          date,
          time,
          comment: event.Notes || "No comment provided.",
          eventType: event.EventType || "Unknown",
        };
      });

      // Sort events by Id in descending order (highest Id first)
      mappedEvents.sort((a, b) => b.id - a.id);

      setEvents(mappedEvents);
      setError(null);
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch a single event by ID
  const fetchEventById = async (id: number): Promise<Event> => {
    const updatedEvents = await getProspectHistoryEvents(prospectId);
    const updatedEvent = updatedEvents.find((event: any) => event.Id === id);

    if (!updatedEvent) {
      throw new Error(`Event with id ${id} not found.`);
    }

    return {
      id: updatedEvent.Id,
      prospect: updatedEvent.ProspectStatus,
      consultant:
        updatedEvent.Consultants?.map((c: any) => c.Name).join(", ") || "N/A",
      date: updatedEvent.Date,
      time: updatedEvent.Time,
      comment: updatedEvent.Notes || "No comment provided.",
      eventType: updatedEvent.EventType || "Unknown",
    };
  };

  // Function to handle event deletion
  const handleDelete = (id: number) => {
    setEvents((prevEvents) => prevEvents.filter((event) => event.id !== id));
  };

  useEffect(() => {
    fetchEvents();
  }, [prospectId]);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    fetchEvents(); // Refresh events after modal closes
  };

  useEffect(() => {
    const eventTypes = getProspectHistoryEventTypes();
  });

  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Typography className={styles.loadingContainer}>
          <CircularProgress />
        </Typography>
      </TableContainer>
    );
  }

  if (error) {
    return (
      <TableContainer component={Paper}>
        <Typography className={styles.errorContainer}>{error}</Typography>
      </TableContainer>
    );
  }

  return (
    <Box className={styles.contentBox}>
      <Box className={styles.buttonContainer}>
        <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
          ← Return
        </Button>
        <Button variant="contained" color="primary" onClick={handleModalOpen}>
          + New Event
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell className={styles.tableCell}>Status</TableCell>
              <TableCell className={styles.tableCell}>Type</TableCell>
              <TableCell className={styles.commentCell}>Comment</TableCell>
              <TableCell className={styles.tableCell}>Date</TableCell>
              <TableCell className={styles.tableCell}>Time</TableCell>
              <TableCell className={styles.tableCell}>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className={styles.noEventsCell}>
                  There are no events for this prospect.
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  fetchEventById={fetchEventById}
                  onDelete={handleDelete}
                  eventTypes={eventTypes}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <AddEventModal
        open={isModalOpen}
        onClose={handleModalClose}
        prospectId={prospectId}
        prospectStatusId={prospectStatusId}
      />
    </Box>
  );
}

export default EventsTable;
